import clsx from "clsx";
import React, { forwardRef, MouseEvent, ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";

type TabStyledRootProps = {
  isSticky: boolean;
  backgroundColor: string;
  fitHeight: boolean;
};

export const TabStyledRoot = styled.div<TabStyledRootProps>`
  background-color: ${({ backgroundColor }) =>
    backgroundColor || "transparent"};
  display: flex;
  align-items: ${({ fitHeight }) => (fitHeight ? "stretch" : "center")};
  gap: 0.5rem;
  font-size: var(--font-size-small);
  width: 100%;
  max-width: 100%;
  flex-wrap: wrap;
  animation: slide-down 0.15s ease-out;

  .nav-link {
    align-items: center;
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem 0.625rem;
    border-radius: var(--rounded-corner);
    color: var(--gray);
    white-space: nowrap;
    background: var(--white);
    box-shadow: var(--drop-shadow-light);
    border: 1px solid var(--border-gray-light);
    animation: scale-from-small 0.3s ease-out;
    font-weight: var(--font-weight-bold);

    &:hover {
      color: var(--primary-black);
      background: var(--page-background);
      border: 1px solid var(--border-gray-light);
      text-decoration: none;
    }
    &.active {
      color: var(--dark-green);
      background: var(--background-green-solid);
      border: 1px solid var(--highlight-green);
      &:hover {
        border: 1px solid var(--green);
      }
    }

    &.disabled {
      background-color: var(--background-gray);
      cursor: not-allowed;
    }
  }

  ${({ isSticky }) =>
    isSticky &&
    `
    position: sticky;
    top: 0;
    z-index: 10002;
  `}

  @media only screen and (max-width: 40rem) {
    top: 0;
    width: 100%;
    overflow-x: auto;
    .tab-container {
      flex-wrap: nowrap;
    }
  }
`;

export type NavItem =
  | {
      to: string;
      display: string | ReactElement;
      active: boolean;
      onClick?: () => void;
      disabled?: boolean;
    }
  | {
      to?: never;
      display: string | ReactElement;
      active: boolean;
      onClick: () => void;
      disabled?: boolean;
    };

export type Props = {
  rightAttachment?: ReactNode;
  backgroundColor?: string;
  isSticky?: boolean;
  className?: string;
  fitHeight?: boolean;
  style?: React.CSSProperties;
} & (
  | { navItems: NavItem[]; children?: never }
  | { children: ReactNode; navItems?: never }
);

export const TabNav = forwardRef<HTMLDivElement, Props>(
  (
    {
      navItems,
      rightAttachment,
      isSticky = true,
      className,
      backgroundColor,
      fitHeight = false,
      children,
      style,
    },
    ref,
  ) => {
    return (
      <TabStyledRoot
        backgroundColor={`${backgroundColor ? backgroundColor : ""}`}
        isSticky={isSticky}
        className={className}
        fitHeight={fitHeight}
        ref={ref}
        style={style}
      >
        <div className="row flex-wrap tab-container">
          {navItems
            ? navItems.map((navItem, index) => (
                <Link
                  className={`nav-link ${navItem.active ? "active" : ""} ${
                    navItem.disabled ? "disabled" : ""
                  }`}
                  key={index}
                  onClick={(event: MouseEvent) => {
                    if (navItem.disabled) {
                      event.preventDefault();
                      return;
                    }
                    if (navItem.onClick) {
                      event.preventDefault();
                      navItem.onClick();
                    }
                  }}
                  to={!navItem.disabled ? navItem.to ?? "" : "#"}
                >
                  {navItem.display}
                </Link>
              ))
            : children}
        </div>
        {rightAttachment}
      </TabStyledRoot>
    );
  },
);

type TabNavItemProps = React.HTMLAttributes<HTMLDivElement> & {
  active: boolean;
  disabled?: boolean;
};

export const TabNavItem = ({
  active,
  children,
  disabled,
  className,
  ...rest
}: TabNavItemProps) => {
  return (
    <div
      className={clsx([
        "nav-link",
        active && "active",
        disabled && "disabled",
        className,
      ])}
      {...rest}
    >
      {children}
    </div>
  );
};

const SplitTabNavItemStyledRoot = styled.div`
  display: flex;
  gap: 0;

  .nav-link {
    border-top-right-radius: 0;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 0;
  }

  .extra-content {
    border-left: 0px solid transparent;
    border-top-right-radius: 4px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 4px;
    border-style: solid;
    border-color: var(--N400);
    border-width: 1px;
    padding: 4px;
    display: flex;
    align-items: center;

    &.active {
      border-color: var(--highlight-green);
    }

    &.disabled {
      border-color: var(--N200);
    }
  }
`;

export const SplitTabNavItem = ({
  active,
  children,
  disabled,
  className,
  extraContent,
  ...rest
}: TabNavItemProps & { extraContent: ReactNode }) => {
  return (
    <SplitTabNavItemStyledRoot>
      <div
        className={clsx([
          "nav-link",
          active && "active",
          disabled && "disabled",
          className,
        ])}
        {...rest}
      >
        {children}
      </div>
      <div
        className={clsx([
          "extra-content",
          active && "active",
          disabled && "disabled",
          className,
        ])}
      >
        {extraContent}
      </div>
    </SplitTabNavItemStyledRoot>
  );
};

TabNav.displayName = "TabNav";
